import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useFetchQuestions from "../../hooks/useFetchQuestions";
import useSaveAnswer from "../../hooks/useSaveAnswer";
import useTestStatus from "../../hooks/useTestStatus";
import "./TestScreen.css";

export default function TestScreen() {
    
    const { domainId, packId, interviewId } = useParams();
    const navigate = useNavigate();

    const { questions, loadingQuestions } = useFetchQuestions(domainId, packId, interviewId);
    const { saveAnswer, fetchLastAttemptIndex } = useSaveAnswer(domainId, packId, interviewId);
    const { markAsFinished, status, loadingStatus } = useTestStatus(domainId, packId, interviewId);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [listening, setListening] = useState(false);
    const [text, setText] = useState("");

    const recognitionRef = useRef(null);
    const finalTranscriptRef = useRef("");
    const listeningRef = useRef(false);
    const transcriptRef = useRef(null);
    const lastCopyFromSelfRef = useRef(0);
    
    document.addEventListener("contextmenu", e => e.preventDefault());

    useEffect(() => {
        const handleContextMenu = (e) => {
            if (e.target.closest(".no-copy")) e.preventDefault();
        };

        const handleCopy = (e) => {
            if (e.target.closest(".no-copy")) {
                e.preventDefault();
                return;
            }
            if (transcriptRef.current && transcriptRef.current.contains(e.target)) {
                lastCopyFromSelfRef.current = Date.now();
            }
        };

        document.addEventListener("contextmenu", handleContextMenu);
        document.addEventListener("copy", handleCopy);
        return () => {
            document.removeEventListener("contextmenu", handleContextMenu);
            document.removeEventListener("copy", handleCopy);
        };
    }, []);

    // Resume unfinished test
    useEffect(() => {
        const initResume = async () => {
            if (questions.length > 0 && (!status || status.status !== "finished")) {
                const last = await fetchLastAttemptIndex(questions);
                setCurrentIndex(last);
            }
        };
        initResume();
    }, [questions, status]);

    // Initialize speech recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert("Speech Recognition not supported in this browser");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        recognition.onresult = (event) => {
            let interim = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript.trim();
                if (event.results[i].isFinal) {
                    finalTranscriptRef.current += transcript + " ";
                } else {
                    interim += transcript + " ";
                }
            }
            setText(finalTranscriptRef.current + interim);
        };

        recognition.onerror = () => setListening(false);
        recognition.onend = () => {
            if (listeningRef.current) recognition.start();
        };

        recognitionRef.current = recognition;
        return () => recognition.stop();
    }, []);

    const handleStartListening = () => {
        if (!recognitionRef.current) return;
        finalTranscriptRef.current = "";
        setText("");
        listeningRef.current = true;
        recognitionRef.current.start();
        setListening(true);
    };

    const handleStopListening = () => {
        if (!recognitionRef.current) return;
        listeningRef.current = false;
        recognitionRef.current.stop();
        setListening(false);
    };

    const handleClear = () => {
        setText("");
        finalTranscriptRef.current = "";
    };

    const handleNext = async () => {
        if (listening) {
            handleStopListening();
            await new Promise((r) => setTimeout(r, 300));
        }

        try {
            await saveAnswer(questions[currentIndex], currentIndex, text);
        } catch (err) {
            console.error("Save error:", err);
            alert("Failed to save answer. Try again.");
            return;
        }

        if (currentIndex < questions.length - 1) {
            setCurrentIndex((i) => i + 1);
            finalTranscriptRef.current = "";
            setText("");
        } else {
            await markAsFinished(questions.length);
            alert("✅ Test completed! All answers saved.");
            navigate(`/user/checkresult/${domainId}/${packId}/${interviewId}`);
        }
    };

    if (loadingQuestions || loadingStatus) return <p className="loading-text">Loading questions...</p>;
    if (status?.status === "finished") return <p className="no-questions">✅ You’ve already completed this test.</p>;
    if (!questions || questions.length === 0) return <p className="no-questions">No questions found.</p>;

    const currentQuestion = questions[currentIndex];

    return (
        <div className="test-page">
            <header className="test-header">
                <div className="top-bar">
                    <div className="instructions">
                        There are around 20–30 questions. You can skip if unsure.
                    </div>
                    <button className="btn next-top" onClick={handleNext}>
                        {currentIndex < questions.length - 1 ? "Next ➡️" : "Finish ✅"}
                    </button>
                </div>
            </header>

            {/* ✅ Scrollable container so answers never go below screen */}
            <div className="test-scroll-container">
                <main className="test-main">
                    <section className="question-card no-copy">
                        <h3>Q{currentIndex + 1}.</h3>
                        <pre className="question-text">
                            <code>{currentQuestion.name || currentQuestion.question}</code>
                        </pre>
                    </section>



                    <section className="answer-area">
                        <p className="hint">
                            Click <b>Start Recording</b> to speak naturally, or type your answer below.
                        </p>

                        <div className="controls">
                            {!listening ? (
                                <button className="btn start" onClick={handleStartListening}>
                                    🎙️ Start Recording
                                </button>
                            ) : (
                                <button className="btn stop" onClick={handleStopListening}>
                                    🛑 Stop
                                </button>
                            )}
                            <button className="btn clear" onClick={handleClear}>
                                🧹 Clear Answer
                            </button>
                        </div>

                        <textarea
                            ref={transcriptRef}
                            className="transcript-box"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onCopy={(e) => {
                                // Mark content copied from this textarea
                                const copiedText = window.getSelection().toString();
                                transcriptRef.current.dataset.lastCopied = copiedText;
                            }}
                            onPaste={(e) => {
                                const clipboardText = e.clipboardData.getData("text");
                                const lastCopied = transcriptRef.current.dataset.lastCopied || "";

                                // Allow paste only if the clipboard text matches something the user copied from this textarea
                                if (lastCopied && clipboardText.includes(lastCopied)) {
                                    return; // ✅ allow
                                }

                                e.preventDefault();
                                alert("🚫 External paste is disabled. You can only copy & paste text typed here.");
                            }}

                            placeholder="Your voice-to-text answer will appear here..."
                            rows={10}
                        />
                    </section>
                </main>
            </div>
        </div>
    );
}
